import React, { useEffect, useRef, useState } from 'react';
import {
    ActivityIndicator,
    Animated,
    Image,
    Modal,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { Colors } from '@/constants/theme';
import { getStreamClient } from '@/services/stream/client';
import { canCallWhatsApp } from '@/services/calls/callPermission';
import type { StartCallParams } from '@/services/calls/startCallEvent';
import type { CallChannel } from '@/services/calls/types';
import type { UniverseStreamChannel } from '@/services/stream/types';
import { fixAvatarUrl } from '@/utils/avatarUrl';

const WA_NOT_GRANTED = 'WA call permission not granted';
const WA_CHECK_FAILED = 'Could not check the WA call permission';

interface StartCallPromptProps {
    /** The `start_call` request to confirm; `null` hides the prompt. */
    request: StartCallParams | null;
    onClose: () => void;
    /**
     * The guide picked a call type; fires once the dialog has closed with the
     * final `/call` params (`channel` set, `phone_id` only on `pstn`).
     */
    onStartCall: (params: StartCallParams) => void;
}

interface Lookup {
    loading: boolean;
    whatsAppAllowed: boolean;
    whatsAppReason: string;
    name: string;
    image: string;
}

/**
 * "Do you want to start to call?" — shown when the Universe CRM pushes a
 * `start_call` event (the guide clicked a client's number). Same card on web
 * and native. Offers both call types; **WhatsApp Call** is enabled only when
 * the channel's `custom_data.call_*` permission allows it (see
 * `canCallWhatsApp`), otherwise it is disabled with the reason underneath.
 * **Uniphone** (PSTN) is never gated, like the header's Phone action.
 */
export function StartCallPrompt({ request, onClose, onStartCall }: StartCallPromptProps) {
    const [lookup, setLookup] = useState<Lookup | null>(null);
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const closingRef = useRef(false);

    const visible = request !== null;

    // Resolve the WhatsApp permission (and a name/avatar the event may lack)
    // from the Stream channel the call belongs to.
    useEffect(() => {
        if (!request) {
            setLookup(null);
            return;
        }
        let cancelled = false;
        closingRef.current = false;
        setLookup({
            loading: true,
            whatsAppAllowed: false,
            whatsAppReason: '',
            name: request.name,
            image: request.image,
        });
        fadeAnim.setValue(0);
        Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }).start();

        (async () => {
            try {
                const channels = await getStreamClient().queryChannels({ id: request.channel_id });
                if (cancelled) return;
                const channel = channels[0] as UniverseStreamChannel | undefined;
                const allowed = canCallWhatsApp(channel?.data?.custom_data);
                setLookup({
                    loading: false,
                    whatsAppAllowed: allowed,
                    whatsAppReason: allowed ? '' : WA_NOT_GRANTED,
                    name: request.name || channel?.data?.name || 'Contact',
                    image: request.image || channel?.data?.image || '',
                });
            } catch (err) {
                console.error('[calls] start_call channel lookup failed', err);
                if (cancelled) return;
                setLookup({
                    loading: false,
                    whatsAppAllowed: false,
                    whatsAppReason: WA_CHECK_FAILED,
                    name: request.name || 'Contact',
                    image: request.image,
                });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [request, fadeAnim]);

    const close = (after?: () => void) => {
        if (closingRef.current) return;
        closingRef.current = true;
        // Let the dialog finish dismissing before the call is opened: native
        // pushes a full-screen route, and the desktop shell opens a separate
        // window the dialog shouldn't linger behind.
        Animated.timing(fadeAnim, { toValue: 0, duration: 150, useNativeDriver: true }).start(() => {
            onClose();
            after?.();
        });
    };

    const start = (channel: CallChannel) => {
        if (!request || !lookup) return;
        const params: StartCallParams = {
            ...request,
            channel,
            name: lookup.name,
            image: lookup.image,
        };
        // Only a real `phones.id` on a PSTN call — `whatsapp` ignores it and
        // sending it back for a Lead is a 422.
        if (channel !== 'pstn') {
            delete params.phone_id;
        }
        close(() => onStartCall(params));
    };

    const name = lookup?.name || request?.name || 'Contact';
    const image = lookup?.image || request?.image || '';
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    const whatsAppDisabled = !lookup || lookup.loading || !lookup.whatsAppAllowed;

    return (
        <Modal visible={visible} transparent animationType="none" onRequestClose={() => close()}>
            <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
                <TouchableOpacity style={[StyleSheet.absoluteFill, styles.backdrop]} activeOpacity={1} onPress={() => close()} />

                <View style={styles.card}>
                    {image ? (
                        <Image source={{ uri: fixAvatarUrl(image) }} style={styles.avatar} />
                    ) : (
                        <View style={[styles.avatar, styles.avatarFallback]}>
                            <Text style={styles.avatarInitial}>{initial}</Text>
                        </View>
                    )}

                    <Text style={styles.name} numberOfLines={1}>{name}</Text>
                    <Text style={styles.question}>Do you want to start to call?</Text>

                    <View style={styles.actions}>
                        <View style={styles.actionColumn}>
                            <TouchableOpacity
                                style={[styles.btn, styles.btnOutline, whatsAppDisabled && styles.btnOutlineDisabled]}
                                activeOpacity={0.8}
                                disabled={whatsAppDisabled}
                                onPress={() => start('whatsapp')}
                                accessibilityState={{ disabled: whatsAppDisabled }}
                            >
                                {lookup?.loading ? (
                                    <ActivityIndicator size="small" color="#9CA3AF" />
                                ) : (
                                    <FontAwesome name="whatsapp" size={18} color={whatsAppDisabled ? '#9CA3AF' : '#25D366'} />
                                )}
                                <Text style={[styles.btnOutlineText, whatsAppDisabled && styles.btnOutlineTextDisabled]}>
                                    WhatsApp Call
                                </Text>
                            </TouchableOpacity>

                            {lookup && !lookup.loading && !lookup.whatsAppAllowed && (
                                <View style={styles.tooltip}>
                                    <View style={styles.tooltipArrow} />
                                    <Text style={styles.tooltipText}>{lookup.whatsAppReason}</Text>
                                </View>
                            )}
                        </View>

                        <View style={styles.actionColumn}>
                            <TouchableOpacity
                                style={[styles.btn, styles.btnPrimary]}
                                activeOpacity={0.8}
                                onPress={() => start('pstn')}
                            >
                                <FontAwesome name="phone" size={18} color="#fff" />
                                <Text style={styles.btnPrimaryText}>Uniphone</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Animated.View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
    },
    backdrop: {
        backgroundColor: 'rgba(0,0,0,0.45)',
    },
    card: {
        width: '100%',
        maxWidth: 480,
        backgroundColor: '#fff',
        borderRadius: 16,
        paddingHorizontal: 24,
        paddingTop: 32,
        paddingBottom: 48,
        alignItems: 'center',
    },
    avatar: {
        width: 220,
        height: 220,
        borderRadius: 110,
        backgroundColor: '#E5E7EB',
        marginBottom: 28,
    },
    avatarFallback: {
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(20, 165, 198, 0.15)',
    },
    avatarInitial: {
        fontSize: 88,
        fontWeight: '600',
        color: Colors.brand,
    },
    name: {
        fontSize: 22,
        fontWeight: '700',
        color: '#1A1A1A',
        marginBottom: 10,
        textAlign: 'center',
    },
    question: {
        fontSize: 17,
        color: '#333',
        marginBottom: 32,
        textAlign: 'center',
    },
    actions: {
        flexDirection: 'row',
        gap: 16,
        alignSelf: 'stretch',
    },
    actionColumn: {
        flex: 1,
        alignItems: 'center',
    },
    btn: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        borderRadius: 8,
        paddingVertical: 15,
        paddingHorizontal: 12,
    },
    btnOutline: {
        backgroundColor: '#fff',
        borderWidth: 1,
        borderColor: '#CBD5E1',
    },
    btnOutlineDisabled: {
        borderColor: '#E5E7EB',
        backgroundColor: '#FAFAFA',
    },
    btnOutlineText: {
        fontSize: 17,
        fontWeight: '600',
        color: '#1A1A1A',
    },
    btnOutlineTextDisabled: {
        color: '#9CA3AF',
        fontWeight: '500',
    },
    btnPrimary: {
        backgroundColor: Colors.brand,
    },
    btnPrimaryText: {
        fontSize: 17,
        fontWeight: '700',
        color: '#fff',
    },
    tooltip: {
        position: 'absolute',
        top: '100%',
        marginTop: 10,
        alignItems: 'center',
        zIndex: 1,
    },
    tooltipArrow: {
        width: 10,
        height: 10,
        backgroundColor: '#1C1C1E',
        transform: [{ rotate: '45deg' }],
        marginBottom: -6,
    },
    tooltipText: {
        backgroundColor: '#1C1C1E',
        color: '#fff',
        fontSize: 13,
        borderRadius: 4,
        paddingVertical: 6,
        paddingHorizontal: 10,
        overflow: 'hidden',
    },
});
